import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Award,
    Bell,
    Calendar,
    CheckCircle2,
    Clock,
    DollarSign,
    ExternalLink,
    FileText,
    Loader2,
    RefreshCw,
    Send,
    TrendingUp,
    Users,
    UserCheck,
    Wallet,
    Wifi,
} from "lucide-react";
import { toast } from "sonner";
import { araciMerkez } from "@/lib/api";
import AraciMerkezModul from "./AraciMerkezModul";
import AraciLeadActions from "./AraciLeadActions";

const REFRESH_MS = 30000;
const STORAGE_KEY = "araci-merkez-moduller";
const EXPANDED_KEY = "araci-merkez-expanded";

const AYLAR = [
    { value: 0, label: "Tüm yıl" },
    { value: 1, label: "Oca" },
    { value: 2, label: "Şub" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Nis" },
    { value: 5, label: "May" },
    { value: 6, label: "Haz" },
    { value: 7, label: "Tem" },
    { value: 8, label: "Ağu" },
    { value: 9, label: "Eyl" },
    { value: 10, label: "Eki" },
    { value: 11, label: "Kas" },
    { value: 12, label: "Ara" },
];

const YILLAR = [2023, 2024, 2025, 2026, 2027];

const DEFAULT_MODULES = {
    uyarilar: true,
    kpi_gun: true,
    trend: true,
    leadler: true,
    hakedis: true,
    bildirim: true,
    aktif_aracilar: true,
    saglik: true,
};

const MODULE_META = [
    { id: "uyarilar", label: "Uyarılar" },
    { id: "kpi_gun", label: "Bugün / Hafta" },
    { id: "trend", label: "Trend" },
    { id: "leadler", label: "Leadler" },
    { id: "hakedis", label: "Hakediş" },
    { id: "bildirim", label: "Bildirimler" },
    { id: "aktif_aracilar", label: "En aktif" },
    { id: "saglik", label: "Sağlık" },
];

const UYARI_STIL = {
    warning: "border-amber-500/35 bg-amber-500/10 text-amber-100",
    danger: "border-rose-500/35 bg-rose-500/10 text-rose-100",
    info: "border-sky-500/35 bg-sky-500/10 text-sky-100",
};

function formatCurrency(val) {
    if (val == null) return "₺0,00";
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
    }).format(val);
}

function loadModuleState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return { ...DEFAULT_MODULES, ...JSON.parse(raw) };
    } catch {
        /* ignore */
    }
    return { ...DEFAULT_MODULES };
}

function loadExpandedState() {
    try {
        const raw = localStorage.getItem(EXPANDED_KEY);
        if (raw) return JSON.parse(raw);
    } catch {
        /* ignore */
    }
    return {};
}

function KpiCard({ label, value, sub, accent, icon: Icon }) {
    const border =
        accent === "violet"
            ? "border-violet-500/30 bg-violet-500/8"
            : accent === "sky"
              ? "border-sky-500/30 bg-sky-500/8"
              : accent === "gold"
                ? "border-[#D4AF37]/35 bg-[#D4AF37]/8"
                : accent === "emerald"
                  ? "border-emerald-500/30 bg-emerald-500/8"
                  : accent === "rose"
                    ? "border-rose-500/30 bg-rose-500/8"
                    : accent === "amber"
                      ? "border-amber-500/30 bg-amber-500/8"
                      : "border-white/10 bg-[#0B0F19]/60";
    const text =
        accent === "violet"
            ? "text-violet-300"
            : accent === "sky"
              ? "text-sky-300"
              : accent === "gold"
                ? "text-[#F3E5AB]"
                : accent === "emerald"
                  ? "text-emerald-300"
                  : accent === "rose"
                    ? "text-rose-300"
                    : accent === "amber"
                      ? "text-amber-300"
                      : "text-white";

    return (
        <div className={`rounded-xl border p-3 ${border}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                {Icon && <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
            </div>
            <div className={`text-lg md:text-xl font-semibold mt-1 ${text}`}>{value}</div>
            {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function TrendBars({ series = [] }) {
    const max = Math.max(...series.map((d) => (d.lead || 0) + (d.bildirim || 0)), 1);
    return (
        <div className="flex items-end gap-2 h-24 pt-2">
            {series.map((d) => {
                const total = (d.lead || 0) + (d.bildirim || 0);
                const h = Math.round((100 * total) / max);
                return (
                    <div key={d.tarih} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <div className="w-full flex items-end justify-center h-16 bg-black/20 rounded-t-md gap-0.5">
                            <div
                                className="w-[35%] rounded-t bg-[#D4AF37] transition-all"
                                style={{ height: `${Math.max(Math.round((100 * (d.lead || 0)) / max), 2)}%` }}
                            />
                            <div
                                className="w-[35%] rounded-t bg-violet-500 transition-all"
                                style={{ height: `${Math.max(Math.round((100 * (d.bildirim || 0)) / max), 2)}%` }}
                            />
                        </div>
                        <span className="text-[9px] text-slate-500 truncate w-full text-center">{d.tarih}</span>
                    </div>
                );
            })}
        </div>
    );
}

export default function AraciKomutaDashboard({ period, onPeriodChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [modules, setModules] = useState(loadModuleState);
    const [expanded, setExpanded] = useState(loadExpandedState);
    const [showModulePicker, setShowModulePicker] = useState(false);
    const [hakedisBusy, setHakedisBusy] = useState(null);

    const persistModules = useCallback((next) => {
        setModules(next);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
            /* ignore */
        }
    }, []);

    const toggleExpanded = useCallback(
        (id) => {
            const next = { ...expanded, [id]: !expanded[id] };
            setExpanded(next);
            try {
                localStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
            } catch {
                /* ignore */
            }
        },
        [expanded],
    );

    const isOpen = (id) => expanded[id] !== false;

    async function handleHakedisOdendi(hakedisId) {
        setHakedisBusy(hakedisId);
        try {
            await araciMerkez.hakedisOdendi(hakedisId);
            toast.success("Hakediş ödendi — aracıya bildirim/e-posta gönderildi.");
            load(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "İşaretleme başarısız.");
        } finally {
            setHakedisBusy(null);
        }
    }

    const toggleModule = useCallback(
        (id) => {
            persistModules({ ...modules, [id]: !modules[id] });
        },
        [modules, persistModules],
    );

    const load = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            try {
                const params = { yil: period.yil };
                if (period.ay) params.ay = period.ay;
                const res = await araciMerkez.dashboard(params);
                setData(res);
                setLastRefresh(new Date());
            } catch {
                if (!silent) toast.error("Aracı komuta verisi yüklenemedi.");
            } finally {
                setLoading(false);
            }
        },
        [period.yil, period.ay],
    );

    useEffect(() => {
        load();
        const id = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    const chartData = useMemo(() => data?.aylik || [], [data]);
    const badges = data?.modul_badges || {};

    if (loading && !data) {
        return (
            <div className="rounded-xl border border-white/10 p-8 text-center text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                Aracı komuta merkezi yükleniyor…
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <section
                className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#121A2F] via-[#0f1424] to-[#1a1510] p-5"
            >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#F3E5AB] mb-2">
                            <Users className="h-3.5 w-3.5 text-[#D4AF37]" />
                            Aracılar Komuta Merkezi
                        </div>
                        <h2 className="text-lg font-bold text-white">
                            {data?.ay_label || "Özet"} · {period.yil}
                        </h2>
                        <p className="text-xs text-slate-500 mt-1 max-w-xl">
                            Lead, hakediş, bildirim ve portal sağlığı — modülleri açıp kapatabilirsiniz.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-300 bg-emerald-500/10">
                                Portal aktif
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full border border-[#D4AF37]/30 text-[#F3E5AB]">
                                {data?.aktif_araci ?? 0} aktif aracı
                            </span>
                            {lastRefresh && (
                                <span className="text-xs px-2 py-0.5 rounded-full border border-white/10 text-slate-400">
                                    <Clock className="h-3 w-3 inline mr-1" />
                                    {lastRefresh.toLocaleTimeString("tr-TR")}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <select
                            value={period.yil}
                            onChange={(e) =>
                                onPeriodChange({ ...period, yil: Number(e.target.value) })
                            }
                            className="h-8 bg-[#0B0F19] border border-white/10 rounded-lg px-2 text-xs text-slate-200"
                        >
                            {YILLAR.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => load()}
                            disabled={loading}
                            className="h-8 px-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-xs text-[#F3E5AB] inline-flex items-center gap-1 disabled:opacity-50"
                        >
                            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            Yenile
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowModulePicker((v) => !v)}
                            className="h-8 px-3 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-white/5"
                        >
                            Modüller
                        </button>
                        <Link
                            to="/yeni/araci-bildirim-gonder"
                            className="h-8 px-3 rounded-lg bg-[#D4AF37] text-black text-xs font-semibold inline-flex items-center gap-1"
                        >
                            <Send className="h-3.5 w-3.5" /> Bildirim
                        </Link>
                        <a
                            href="https://mbshukuk.com/araci/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="h-8 px-3 rounded-lg border border-white/10 text-xs text-slate-300 inline-flex items-center gap-1 hover:bg-white/5"
                        >
                            <ExternalLink className="h-3.5 w-3.5" /> Portal
                        </a>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-4">
                    {AYLAR.map((a) => {
                        const active = period.ay === a.value;
                        return (
                            <button
                                key={a.value}
                                type="button"
                                onClick={() => onPeriodChange({ ...period, ay: a.value })}
                                className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${
                                    active
                                        ? "bg-[#D4AF37]/80 text-black font-semibold"
                                        : "border border-white/10 text-slate-400 hover:bg-white/5"
                                }`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mt-4">
                    <KpiCard label="Toplam Aracı" value={data?.toplam_araci ?? "—"} sub={`${data?.aktif_araci ?? 0} aktif`} accent="violet" icon={Users} />
                    <KpiCard label="Dönem Kaydı" value={data?.donem_araci ?? "—"} sub="Yeni aracı" accent="sky" icon={UserCheck} />
                    <KpiCard label="Toplam Hakediş" value={formatCurrency(data?.toplam_hakedis)} accent="gold" icon={Wallet} />
                    <KpiCard label="Ödenen" value={formatCurrency(data?.toplam_odenen)} accent="emerald" icon={CheckCircle2} />
                    <KpiCard label="Bekleyen" value={formatCurrency(data?.toplam_bekleyen)} accent="rose" icon={DollarSign} />
                    <KpiCard label="Lead" value={data?.toplam_lead ?? "—"} sub={`${data?.lead_bekleyen ?? 0} bekleyen`} accent="amber" icon={FileText} />
                </div>
            </section>

            {showModulePicker && (
                <div className="rounded-xl border border-white/10 bg-[#0B0F19]/80 p-4">
                    <p className="text-xs text-slate-400 mb-3">Görünür modüller (tıklayarak aç/kapa):</p>
                    <div className="flex flex-wrap gap-2">
                        {MODULE_META.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => toggleModule(m.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                    modules[m.id]
                                        ? "border-[#D4AF37]/40 bg-[#D4AF37]/15 text-[#F3E5AB]"
                                        : "border-white/10 text-slate-500 hover:text-slate-300"
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => persistModules({ ...DEFAULT_MODULES })}
                            className="px-3 py-1.5 rounded-lg text-xs border border-white/10 text-slate-400 hover:bg-white/5"
                        >
                            Tümünü aç
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {modules.uyarilar && (
                    <AraciMerkezModul
                        id="uyarilar"
                        title="Uyarılar ve Aksiyonlar"
                        icon={AlertTriangle}
                        badge={badges.uyarilar}
                        open={isOpen("uyarilar")}
                        onToggle={() => toggleExpanded("uyarilar")}
                        accent="amber"
                    >
                        {(data?.uyarilar || []).length === 0 ? (
                            <p className="text-xs text-slate-500">Şu an kritik uyarı yok.</p>
                        ) : (
                            <div className="space-y-2">
                                {(data?.uyarilar || []).map((u, i) => (
                                    <div
                                        key={i}
                                        className={`rounded-lg border px-3 py-2 text-xs ${UYARI_STIL[u.seviye] || UYARI_STIL.info}`}
                                    >
                                        <div className="font-semibold">{u.baslik}</div>
                                        <p className="opacity-85 mt-0.5">{u.aciklama}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </AraciMerkezModul>
                )}

                {modules.kpi_gun && (
                    <AraciMerkezModul
                        id="kpi_gun"
                        title="Bugün ve Bu Hafta"
                        icon={TrendingUp}
                        open={isOpen("kpi_gun")}
                        onToggle={() => toggleExpanded("kpi_gun")}
                        accent="sky"
                    >
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[10px] uppercase text-slate-500 mb-2">Bugün</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Lead</span>
                                        <span className="block text-amber-300 font-mono">{data?.bugun?.yeni_lead ?? 0}</span>
                                    </div>
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Bildirim</span>
                                        <span className="block text-violet-300 font-mono">{data?.bugun?.yeni_bildirim ?? 0}</span>
                                    </div>
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Ödenen</span>
                                        <span className="block text-emerald-300 font-mono text-[11px]">
                                            {formatCurrency(data?.bugun?.odenen_tutar)}
                                        </span>
                                    </div>
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Yeni aracı</span>
                                        <span className="block text-sky-300 font-mono">{data?.bugun?.yeni_araci ?? 0}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase text-slate-500 mb-2">Bu hafta</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Lead</span>
                                        <span className="block text-amber-300 font-mono">{data?.hafta?.yeni_lead ?? 0}</span>
                                    </div>
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Bildirim</span>
                                        <span className="block text-violet-300 font-mono">{data?.hafta?.yeni_bildirim ?? 0}</span>
                                    </div>
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Ödenen</span>
                                        <span className="block text-emerald-300 font-mono text-[11px]">
                                            {formatCurrency(data?.hafta?.odenen_tutar)}
                                        </span>
                                    </div>
                                    <div className="rounded-lg bg-black/20 px-2 py-1.5">
                                        <span className="text-slate-500">Yeni aracı</span>
                                        <span className="block text-sky-300 font-mono">{data?.hafta?.yeni_araci ?? 0}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AraciMerkezModul>
                )}
            </div>

            {modules.trend && (
                <AraciMerkezModul
                    id="trend"
                    title={period.ay ? `${data?.ay_label} trend` : `${period.yil} aylık kayıt trendi`}
                    icon={Calendar}
                    open={isOpen("trend")}
                    onToggle={() => toggleExpanded("trend")}
                    accent="gold"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 mb-2">Son 7 gün (lead + bildirim)</p>
                            <TrendBars series={data?.trend_7gun || []} />
                            <div className="flex gap-3 text-[10px] text-slate-500 mt-2">
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-[#D4AF37]" /> Lead</span>
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-violet-500" /> Bildirim</span>
                            </div>
                        </div>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                            {(period.ay
                                ? chartData.filter((d) => d.ay === period.ay)
                                : chartData
                            ).map((d) => (
                                <div key={d.ay} className="flex justify-between text-xs py-1 border-b border-white/5">
                                    <span className="text-slate-400">{d.ay_label}</span>
                                    <span className="text-slate-300 font-mono">
                                        {d.yeni_araci} aracı · {d.yeni_lead} lead
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </AraciMerkezModul>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {modules.leadler && (
                    <AraciMerkezModul
                        id="leadler"
                        title="Son Dosya Önerileri (Lead)"
                        icon={FileText}
                        badge={badges.leadler}
                        open={isOpen("leadler")}
                        onToggle={() => toggleExpanded("leadler")}
                        accent="amber"
                    >
                        <ul className="space-y-2">
                            {(data?.son_leadler || []).map((l) => (
                                <li key={l.id} className="text-xs border-b border-white/5 pb-2 last:border-0">
                                    <div className="flex justify-between items-center gap-2">
                                        <span className="text-slate-200 font-medium">{l.ad_soyad}</span>
                                        <span
                                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                l.durum === "ONAYLANDI"
                                                    ? "bg-emerald-500/10 text-emerald-400"
                                                    : l.durum === "REDDEDILDI"
                                                      ? "bg-rose-500/10 text-rose-400"
                                                      : "bg-amber-500/10 text-amber-400"
                                            }`}
                                        >
                                            {l.durum_label}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-slate-500 mt-1">
                                        <span>{l.araci_ad}</span>
                                        <span className="font-mono">{l.plaka}</span>
                                    </div>
                                    {l.bekleme_gun >= 3 && l.durum === "BEKLEMEDE" && (
                                        <span className="text-[10px] text-rose-400">{l.bekleme_gun} gün bekliyor</span>
                                    )}
                                    <AraciLeadActions lead={l} compact onDone={() => load(true)} />
                                </li>
                            ))}
                        </ul>
                        <Link to="/yeni/araci-merkezi?tab=leadler" className="text-[10px] text-[#D4AF37] mt-3 inline-block hover:underline">
                            Tüm lead listesi →
                        </Link>
                    </AraciMerkezModul>
                )}

                {modules.hakedis && (
                    <AraciMerkezModul
                        id="hakedis"
                        title="Bekleyen Ödemeler"
                        icon={DollarSign}
                        badge={badges.hakedis}
                        open={isOpen("hakedis")}
                        onToggle={() => toggleExpanded("hakedis")}
                        accent="rose"
                    >
                        <div className="text-xs text-slate-500 mb-3">
                            Hakediş özeti: ödenen{" "}
                            <span className="text-emerald-400 font-mono">
                                {formatCurrency(data?.hakedis_muvekkil?.odenen)}
                            </span>
                            {" · "}bekleyen{" "}
                            <span className="text-rose-400 font-mono">
                                {formatCurrency(data?.hakedis_muvekkil?.bekleyen)}
                            </span>
                            {data?.hakedis_model === "satir" && (
                                <span className="ml-2 text-[#D4AF37]">· tek model aktif</span>
                            )}
                        </div>
                        <ul className="space-y-2">
                            {(data?.bekleyen_odemeler || []).map((o) => (
                                <li key={o.id} className="flex justify-between items-center text-xs gap-2">
                                    <div>
                                        <span className="text-slate-200">{o.araci_ad}</span>
                                        <span className="text-slate-500 ml-2 font-mono">{o.dosya_no}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-rose-300 font-mono">{formatCurrency(o.tutar)}</span>
                                        {o.hakedis_id && (
                                            <button
                                                type="button"
                                                disabled={hakedisBusy === o.hakedis_id}
                                                onClick={() => handleHakedisOdendi(o.hakedis_id)}
                                                className="px-2 py-0.5 rounded bg-emerald-600/80 text-white text-[10px] font-semibold disabled:opacity-50"
                                            >
                                                {hakedisBusy === o.hakedis_id ? "…" : "Ödendi"}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <Link to="/e/araci-odemeleri" className="text-[10px] text-[#D4AF37] mt-3 inline-block hover:underline">
                            Ödeme kayıtları →
                        </Link>
                    </AraciMerkezModul>
                )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {modules.bildirim && (
                    <AraciMerkezModul
                        id="bildirim"
                        title="Son Bildirimler"
                        icon={Bell}
                        badge={badges.bildirim}
                        open={isOpen("bildirim")}
                        onToggle={() => toggleExpanded("bildirim")}
                        accent="violet"
                    >
                        <ul className="space-y-2">
                            {(data?.son_bildirimler || []).map((b) => (
                                <li key={b.id} className="flex justify-between gap-2 text-xs border-b border-white/5 pb-2">
                                    <div className="min-w-0">
                                        <span className="text-slate-200 block truncate">{b.baslik}</span>
                                        <span className="text-slate-500">{b.araci_ad}</span>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {!b.okundu && (
                                            <span className="text-[10px] text-amber-400 block">Okunmadı</span>
                                        )}
                                        <span className="text-[10px] text-slate-500">{b.tarih}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="flex gap-3 mt-3">
                            <Link to="/e/araci-bildirimler" className="text-[10px] text-[#D4AF37] hover:underline">
                                Tüm bildirimler →
                            </Link>
                            <Link to="/yeni/araci-bildirim-gonder" className="text-[10px] text-sky-400 hover:underline">
                                Gönder →
                            </Link>
                        </div>
                    </AraciMerkezModul>
                )}

                {modules.aktif_aracilar && (
                    <AraciMerkezModul
                        id="aktif_aracilar"
                        title="En Çok Dosya Getiren Aracılar"
                        icon={Award}
                        open={isOpen("aktif_aracilar")}
                        onToggle={() => toggleExpanded("aktif_aracilar")}
                        accent="gold"
                    >
                        <ul className="space-y-2">
                            {(data?.en_aktif_aracilar || []).map((a) => (
                                <li key={a.id} className="flex justify-between text-xs gap-2">
                                    <Link to={`/e/aracilar/${a.id}`} className="text-slate-200 hover:text-[#F3E5AB] truncate">
                                        {a.ad}
                                        {!a.aktif && <span className="text-slate-500 ml-1">(pasif)</span>}
                                    </Link>
                                    <div className="text-right shrink-0">
                                        <span className="text-[#F3E5AB] font-mono block">{a.muvekkil_count} dosya</span>
                                        <span className="text-[10px] text-slate-500 font-mono">{formatCurrency(a.hakedis)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <Link to="/e/aracilar" className="text-[10px] text-[#D4AF37] mt-3 inline-block hover:underline">
                            Aracı listesi →
                        </Link>
                    </AraciMerkezModul>
                )}
            </div>

            {modules.saglik && (
                <AraciMerkezModul
                    id="saglik"
                    title="Portal ve Sistem Sağlığı"
                    icon={Wifi}
                    open={isOpen("saglik")}
                    onToggle={() => toggleExpanded("saglik")}
                    accent="emerald"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div className="rounded-lg bg-black/20 px-3 py-2">
                            <span className="text-slate-500 block">Portal</span>
                            <span className="text-emerald-400 font-medium">Erişilebilir</span>
                        </div>
                        <div className="rounded-lg bg-black/20 px-3 py-2">
                            <span className="text-slate-500 block">Aktif oran</span>
                            <span className="text-sky-300 font-mono">{data?.saglik?.aktif_araci_orani ?? 0}%</span>
                        </div>
                        <div className="rounded-lg bg-black/20 px-3 py-2">
                            <span className="text-slate-500 block">E-posta bildirim</span>
                            <span className="text-violet-300 font-mono">{data?.saglik?.bildirim_email_acik ?? 0} aracı</span>
                        </div>
                        <div className="rounded-lg bg-black/20 px-3 py-2">
                            <span className="text-slate-500 block">Okunmamış</span>
                            <span className="text-amber-300 font-mono">{data?.saglik?.okunmamis_bildirim ?? 0}</span>
                        </div>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-3">
                        Portal: {data?.saglik?.portal_url} · Son güncelleme: {data?.saglik?.son_guncelleme}
                    </p>
                </AraciMerkezModul>
            )}
        </div>
    );
}
